import { CalendarCheck, CalendarDays, Clock, History, Percent, UserX } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@/components/feedback/States'
import { Badge } from '@/components/ui/Badge'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { StatCard } from '@/components/ui/StatCard'
import { cn } from '@/utils/cn'
import { formatDate } from '@/utils/format'
import { formatLongDay, formatShortDay, monthGrid, parseIso, weekdaysMondayFirst } from '../dates'
import { STATUSES, formatRate, statusCellClass, statusMeta } from '../status'
import type { StudentAttendance } from '../types'
import { MonthNav } from './MonthNav'

/** A student's attendance for a period: KPIs, a month calendar and the most recent days. */
export function StudentAttendanceView({ data }: { data: StudentAttendance }) {
  const { t } = useTranslation('attendance')
  const [month, setMonth] = useState(
    data.today.slice(0, 7) < data.to.slice(0, 7) ? data.today.slice(0, 7) : data.to.slice(0, 7),
  )
  const s = data.summary
  const byDate = new Map(data.days.map((d) => [d.date, d]))
  const holidays = new Map(data.holidays.map((h) => [h.date, h]))
  const closed = new Set(data.closedWeekdays)
  const recent = data.days.slice(0, 10)
  const minMonth = data.from.slice(0, 7)

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t('Attendance rate')}
          value={formatRate(s.rate)}
          icon={<Percent />}
          tone={s.rate === null || s.rate >= 90 ? 'success' : s.rate >= 80 ? 'warning' : 'danger'}
          hint={`${data.academicYear ?? t('This period')} · ${formatDate(data.from)} – ${formatDate(data.to)}`}
        />
        <StatCard
          label={t('Days present')}
          value={s.present + s.late}
          icon={<CalendarCheck />}
          tone="brand"
          hint={t('{{marked}} of {{count}} school days recorded', {
            marked: s.marked,
            count: data.workingDays,
          })}
        />
        <StatCard
          label={t('Absences')}
          value={s.absent}
          icon={<UserX />}
          tone="danger"
          hint={`${t('{{count}} excused', { count: s.excused })} · ${t('{{count}} half days', { count: s.halfDay })}`}
        />
        <StatCard
          label={t('Late arrivals')}
          value={s.late}
          icon={<Clock />}
          tone="warning"
          hint={t('Counted as present')}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader
            title={t('Calendar')}
            icon={<CalendarDays />}
            actions={
              <MonthNav
                month={month}
                onChange={(m) => setMonth(m < minMonth ? minMonth : m)}
                max={data.today.slice(0, 7)}
              />
            }
          />
          <CardBody className="space-y-4">
            <table className="w-full table-fixed border-separate border-spacing-1">
              <caption className="sr-only">
                {t('Attendance of {{name}} by day. The recent days list repeats this information.', {
                  name: data.student.fullName,
                })}
              </caption>
              <thead>
                <tr>
                  {weekdaysMondayFirst().map((w) => (
                    <th
                      key={w.short}
                      scope="col"
                      className="pb-1 text-2xs font-semibold tracking-wider text-muted uppercase"
                    >
                      <abbr title={w.long} className="no-underline">
                        {w.short}
                      </abbr>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthGrid(month).map((week, i) => (
                  <tr key={i}>
                    {week.map((date, j) => {
                      if (!date) return <td key={j} />
                      const mark = byDate.get(date)
                      const holiday = holidays.get(date)
                      const isClosed = closed.has(parseIso(date).getDay())
                      const text = mark
                        ? t(statusMeta[mark.status].label) + (mark.note ? ` — ${mark.note}` : '')
                        : holiday
                          ? t('Holiday: {{name}}', { name: holiday.name })
                          : isClosed
                            ? t('School closed')
                            : date > data.today
                              ? t('Upcoming')
                              : t('No record')
                      return (
                        <td key={j} className="p-0">
                          <div
                            role="img"
                            aria-label={`${formatLongDay(date)}: ${text}`}
                            title={text}
                            className={cn(
                              'flex h-11 flex-col items-center justify-center rounded-xs text-xs',
                              mark
                                ? statusCellClass[mark.status]
                                : holiday || isClosed
                                  ? 'bg-surface-sunken text-subtle'
                                  : 'text-muted',
                              date === data.today && 'ring-2 ring-focus ring-offset-1 ring-offset-surface',
                            )}
                          >
                            <span className="font-medium">{Number(date.slice(8))}</span>
                            {mark ? (
                              <span className="text-2xs font-semibold" aria-hidden="true">
                                {t(statusMeta[mark.status].short)}
                              </span>
                            ) : null}
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <ul className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted" aria-label={t('Legend')}>
              {STATUSES.map((st) => (
                <li key={st} className="inline-flex items-center gap-1.5">
                  <span
                    className={cn(
                      'grid size-4 place-items-center rounded-xs text-2xs font-semibold',
                      statusCellClass[st],
                    )}
                    aria-hidden="true"
                  >
                    {t(statusMeta[st].short)}
                  </span>
                  {t(statusMeta[st].label)}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title={t('Recent days')}
            icon={<History />}
            description={
              data.unmarkedDays > 0
                ? t('{{count}} school days not recorded yet', { count: data.unmarkedDays })
                : undefined
            }
          />
          {recent.length === 0 ? (
            <EmptyState
              compact
              title={t('No attendance recorded yet')}
              description={t('Marks appear here once the class teacher takes the register.')}
            />
          ) : (
            <ul className="divide-y divide-line">
              {recent.map((d) => (
                <li key={d.date} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink">{formatShortDay(d.date)}</p>
                    {d.note ? <p className="truncate text-xs text-muted">{d.note}</p> : null}
                  </div>
                  <Badge tone={statusMeta[d.status].tone} dot>
                    {t(statusMeta[d.status].label)}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  )
}
